/**
 * Global Admin Context
 *
 * Provides platform admin status as a single source of truth.
 * This context is mounted once near the root and shares the admin status
 * with all consumers, avoiding duplicate Firestore reads/subscriptions.
 *
 * Platform admin is determined by: /users/{uid}.isPlatformAdmin === true
 * Legacy support: /users/{uid}.role === 'admin'
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from '@/lib/logger';

interface GlobalAdminContextType {
  isPlatformAdmin: boolean;
  isLoading: boolean;
}

const GlobalAdminContext = createContext<GlobalAdminContextType>({
  isPlatformAdmin: false,
  isLoading: true,
});

interface GlobalAdminProviderProps {
  children: ReactNode;
}

export function GlobalAdminProvider({ children }: GlobalAdminProviderProps) {
  const { user, isLoading: authLoading } = useAuth();
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Wait for auth to resolve
    if (authLoading) {
      setIsLoading(true);
      return;
    }

    if (!user) {
      setIsPlatformAdmin(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Path-specific logging for Firestore calls
    logger.info(`[GlobalAdminProvider] onSnapshot users/${user.uid} (admin check)`);

    // Subscribe to user document to check isPlatformAdmin field
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(
      userRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const userData = snapshot.data();
          // Check new field first, then legacy field for migration
          const hasNewFlag = userData?.isPlatformAdmin === true;
          const hasLegacyRole = userData?.role === 'admin';
          const isAdmin = hasNewFlag || hasLegacyRole;

          if (isAdmin) {
            logger.info('[GlobalAdminProvider] User is platform admin', {
              isPlatformAdmin: hasNewFlag,
              legacyRole: hasLegacyRole,
            });
          }

          setIsPlatformAdmin(isAdmin);
        } else {
          // User document missing - treat as non-admin (fail safely)
          setIsPlatformAdmin(false);
        }
        setIsLoading(false);
      },
      (error) => {
        // Error loading - treat as non-admin (fail safely)
        logger.error('[GlobalAdminProvider] Error checking admin status:', error);
        setIsPlatformAdmin(false);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, authLoading]);

  return (
    <GlobalAdminContext.Provider value={{ isPlatformAdmin, isLoading }}>
      {children}
    </GlobalAdminContext.Provider>
  );
}

/**
 * Hook to access platform admin status from the global context.
 * This is the preferred way to check admin status - it uses the shared
 * subscription and avoids duplicate Firestore reads.
 */
export function useGlobalAdminContext(): GlobalAdminContextType {
  const context = useContext(GlobalAdminContext);
  if (context === undefined) {
    throw new Error('useGlobalAdminContext must be used within a GlobalAdminProvider');
  }
  return context;
}
